import React, {ChangeEvent, useEffect, useState} from 'react';
import AsyncSelect from 'react-select/async';

const debounce = require('debounce-promise');

import {FieldProp, WorkflowItem} from '../../../types';
import {Modal} from '../../Modal';
import {Etl} from '../../../_services/Etl';
import {Flow, FlowFetchResponse} from '../../../_services/Flow';
import {Alert} from '../../Alert';

type TorqueBuLookupProps = FieldProp & {};

export const TorqueBuLookup: React.FunctionComponent<TorqueBuLookupProps> = ({
                                                                                 form,
                                                                                 field,
                                                                                 displayOnly
                                                                             }): JSX.Element => {

    const [showModal, setShowModal] = useState<boolean>(false);
    const [formState, setFormState] = useState<any>({});
    const [userIsWorkflowAdmin, setUserIsWorkflowAdmin] = useState<boolean>(false);

    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [fileInputKey, setFileInputKey] = useState<number>(1);
    const [fileIsUploading, setFileIsUploading] = useState<boolean>(false);
    const [fileUploadMessage, setFileUploadMessage] = useState<any>({});

    const loadAsyncOptions = debounce(
        (inputValue: string) => {
            const minimumInputLength: number = 3;
            const resultProperty = 'result';
            const resultValueProperty = 'sku';
            const resultTextProperty = 'sku';
            const pipelineName = 'torque-sku-autocompleter';

            if (inputValue.length < minimumInputLength) return;

            return Etl.getInstance().process(pipelineName, {q: inputValue})
                .then((response: FlowFetchResponse) => {
                    return response.json[resultProperty].map((item: any) => ({
                        value: item[resultValueProperty],
                        label: item[resultTextProperty],
                        text: item[resultTextProperty],
                        ...item
                    }));
                });
        },
        250
    );

    // for the time being, the easiest solution is to get all the workflows then find the current workflow to see if
    // current user is an admin
    useEffect(() => {
        Flow.getInstance().getWorkflows()
            .then((response: FlowFetchResponse) => {
                    if (response.ok && response?.json?.workflows?.length > 0 && form?.id) {
                        const workflows: WorkflowItem[] = response.json.workflows
                        const userIsAdmin = workflows.some((workflow: WorkflowItem) => {
                            return workflow.id as unknown as string === form.id && workflow.isAdmin;
                        });
                        setUserIsWorkflowAdmin(userIsAdmin);
                    }
                }
            );
    }, [false]);

    const handleFileUpload = (file: File) => {
        setFileIsUploading(true);
        setFileUploadMessage({});

        Flow.getInstance().uploadNewTorqueSkuToBuFile(file)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setFileUploadMessage({type: 'success', message: 'New file successfully loaded'});
                } else {
                    throw Error(response.json.message || 'Error uploading file');
                }
            })
            .catch((error: any) => {
                setFileUploadMessage({type: 'danger', message: error?.message || error || 'Error uploading file'});
            })
            .finally(() => {
                setFileIsUploading(false);
                setFileInputKey(fileInputKey + 1);
            })
    };

    const buttonLabel = field?.flowCustomDisplayComponentConfig?.buttonLabel ?
        field.flowCustomDisplayComponentConfig.buttonLabel
        :
        'Lookup Business Unit';

    const modalTitle = field?.flowCustomDisplayComponentConfig?.modalTitle ?
        field.flowCustomDisplayComponentConfig.modalTitle
        :
        'Business Unit Lookup';

    const resultTextLabel = field?.flowCustomDisplayComponentConfig?.resultTextLabel ?
        field.flowCustomDisplayComponentConfig.resultTextLabel
        :
        'Business Unit';

    const fieldLabel = field?.flowCustomDisplayComponentConfig?.fieldLabel ?
        field.flowCustomDisplayComponentConfig.fieldLabel
        :
    'Enter SKU Below:';

    return (
        displayOnly ?
            <div/>
            :
            <div>
                <button type="button"
                        className="btn btn-secondary mt-1"
                        onClick={() => {
                            setShowModal(true);
                        }}
                >{buttonLabel}
                </button>
                {showModal &&
                <Modal>
                    <div className="modal-header">
                        <h5 className="modal-title">{modalTitle}</h5>
                        <button type="button"
                                className="close"
                                aria-label="Close"
                                tabIndex={-1}
                                onClick={() => {
                                    setShowModal(false);
                                }}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="container">
                            <div className="row mb-2">
                                <div className="col">
                                    <label>{fieldLabel}</label>
                                    <AsyncSelect
                                        value={formState.sku}
                                        onChange={(option: any) => {
                                            setFormState({
                                                ...formState,
                                                sku: option
                                            });
                                        }}
                                        loadOptions={loadAsyncOptions}
                                        isClearable={true}
                                    />

                                    {formState?.sku?.abb_ph1 &&
                                    <div>
                                        <h4 className="m-2">{resultTextLabel} for <em>{formState?.sku?.label}</em>:</h4>
                                        <h6 className="m-0">{formState?.sku?.abb_ph1}</h6>
                                    </div>
                                    }
                                </div>
                            </div>
                            {showEdit &&
                            <div className="container">
                                <div className="row mb-2">
                                    <div className="col">
                                        <p>To edit the SKU to BU relationships, <a href="/p/flow/api/torque-sku-to-bu">Download</a> the existing data, and re-upload below</p>
                                        <p><small><em><strong>Note:</strong> do not change the ordering of any of the columns, nor remove the first row.</em></small></p>
                                        <div className="form-group">
                                            <input type="file"
                                                   key={fileInputKey}
                                                   className="form-control-file"
                                                   onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                       e.target &&
                                                       e.target.files &&
                                                       e.target.files.length &&
                                                       handleFileUpload(e.target.files[0]);
                                                   }}
                                            />
                                            {fileUploadMessage?.message &&
                                            <Alert
                                                type={fileUploadMessage?.type}
                                                message={fileUploadMessage.message}
                                            />}
                                            {fileIsUploading &&
                                            <div>
                                                <i className="fas fa-spinner fa-spin mr-1"/>
                                                Uploading new file, this can take up to 5 minutes...
                                            </div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    <div className="modal-footer">
                        {userIsWorkflowAdmin && !showEdit &&
                        <button
                            type="button"
                            className="btn btn-dark mr-auto"
                            onClick={() => {
                                setShowEdit(true);
                            }}
                        >Edit</button>
                        }

                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowModal(false)
                            }}
                        >Close
                        </button>
                    </div>
                </Modal>
                }
            </div>
    );
};
